import { EMPTY_FIELD } from 'constants/emptyField';
import { OperationAccess } from 'constants/operationAccesses';
import { Resources } from 'constants/resources';

import DataObjectIcon from '@mui/icons-material/DataObject';
import { blue } from '@mui/material/colors';
import { usePermissions } from 'hooks/usePermissions';
import { Button } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowDialogButton,
} from 'shared/mui/ContextMenu';
import {
  DateTimeField,
  FunctionField,
  TextField,
  TooltipTextField,
} from 'shared/react-admin/Fields';
import { MerchantCredentials } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';

import { DatagridStyled } from './MerchantsShowCredentialsDatagrid.styled';

type Props = {
  handleOpenShowDialog: (record?: MerchantCredentials) => void;
  handleOpenEditDialog: (record?: MerchantCredentials) => void;
  handleOpenDeleteDialog: (record?: MerchantCredentials) => void;
  handleOpenCredentialsDialog: (record?: MerchantCredentials) => void;
  handleOpenRecordModalProvider: (record?: Provider) => void;
};

export const MerchantsShowCredentialsDatagrid: React.FC<Props> = ({
  handleOpenShowDialog,
  handleOpenEditDialog,
  handleOpenDeleteDialog,
  handleOpenRecordModalProvider,
  handleOpenCredentialsDialog,
}) => {
  const { hasResourcePermissions, hasResourceAccess, hasOperationAccess } = usePermissions();

  return (
    <DatagridStyled bulkActionButtons={false}>
      <TextField label="ID" sortBy="id" source="originId" />
      <FunctionField
        label="Название"
        render={(record: MerchantCredentials) => (
          <TextField
            onClick={() => handleOpenShowDialog(record)}
            source="name"
            sx={{
              cursor: 'pointer',
              color: blue[700],
              textDecoration: 'underline',
            }}
          />
        )}
        source="name"
      />
      <FunctionField
        cellClassName="column-provider"
        label="Провайдер"
        render={(record: { provider?: Provider }) =>
          record?.provider ? (
            <TextField
              onClick={() => handleOpenRecordModalProvider(record.provider)}
              record={record.provider}
              source="name"
              sx={{
                cursor: 'pointer',
                color: blue[700],
                textDecoration: 'underline',
              }}
            />
          ) : (
            EMPTY_FIELD
          )
        }
      />
      <TooltipTextField label="Комментарий" sortable={false} source="comment" />
      <FunctionField
        label="Реквизиты доступа {json}"
        render={(record: MerchantCredentials) =>
          hasOperationAccess(OperationAccess.ManageMerchantCredentials) ? (
            <Button
              label="Показать"
              onClick={() => handleOpenCredentialsDialog(record)}
              size="small"
              startIcon={<DataObjectIcon />}
            />
          ) : (
            <Button disabled label="НЕТ ДОСТУПА" size="small" startIcon={<DataObjectIcon />} />
          )
        }
        sortable={false}
        source="credentials"
      />
      <DateTimeField label="Дата создания" source="createdAt" />
      <DateTimeField label="Дата редактир." source="updatedAt" />
      <ContextMenu
        render={(record: MerchantCredentials) => [
          hasResourceAccess(Resources.UserActions) && (
            <ContextMenuEntityUserActionsButton
              entityName="merchant_credentials"
              key="user-action-link"
            />
          ),
          hasResourcePermissions('merchant_credentials', [Permission.Full, Permission.Read]) && (
            <ContextMenuShowDialogButton
              key="show-button"
              onClick={() => handleOpenShowDialog(record)}
            />
          ),
          hasResourcePermissions('merchant_credentials', [Permission.Full, Permission.Update]) &&
            !record?.locked && (
              <ContextMenuEditDialogButton
                key="edit-button"
                onClick={() => handleOpenEditDialog(record)}
              />
            ),
          hasResourcePermissions('merchant_credentials', [Permission.Full, Permission.Delete]) && (
            <ContextMenuDeleteButton
              key="delete-button"
              onClick={() => handleOpenDeleteDialog(record)}
            />
          ),
        ]}
      />
    </DatagridStyled>
  );
};
